import { useQuery } from '@tanstack/react-query';
import { OPENWEATHERMAP_API_KEY } from '../../pages/components/map/weatherConstants';

/**
 * Which field of OpenWeather's current-conditions response each map layer
 * reads, and how to render it. The tile API the map draws from returns
 * imagery, not numbers, so the legend's current value needs this separate
 * point call.
 */
const READERS = {
  temp: (d) => d?.main?.temp,
  precip: (d) => d?.rain?.['1h'] ?? d?.snow?.['1h'] ?? 0,
  clouds: (d) => d?.clouds?.all,
  wind: (d) => d?.wind?.speed,
  pressure: (d) => d?.main?.pressure,
};

/** Rounded so panning a few pixels does not start a new request. */
const key = (n) => (Number.isFinite(n) ? Math.round(n * 2) / 2 : null);

/**
 * The value of `layer` at a point, for the legend's readout.
 *
 * Returns `null` rather than throwing or guessing when the call fails — the
 * data API is a different OpenWeather product from the map tiles, so a key
 * that renders tiles may still be refused here. The legend simply omits the
 * readout in that case, which is honest; showing a stale or invented number
 * beside a live map would not be.
 */
export const useWeatherPointValue = (layer, center) => {
  const lat = key(center?.lat);
  const lon = key(center?.lng);
  const enabled = Boolean(OPENWEATHERMAP_API_KEY && lat != null && lon != null && READERS[layer]);

  const { data } = useQuery({
    queryKey: ['weatherPoint', lat, lon],
    enabled,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: false, // a refused data-API key will not start working on retry
    queryFn: async () => {
      const url = `https://api.openweathermap.org/data/2.5/weather?lat=${lat}&lon=${lon}`
        + `&units=metric&appid=${OPENWEATHERMAP_API_KEY}`;
      const res = await fetch(url);
      if (!res.ok) return null;
      return res.json();
    },
  });

  if (!enabled || !data) return null;
  const raw = READERS[layer]?.(data);
  return Number.isFinite(raw) ? raw : null;
};

export default useWeatherPointValue;
